import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Loader2, Save, X, ChevronDown, ChevronUp, Shield, FileText, ShoppingCart, ClipboardList, ToggleLeft, ToggleRight } from 'lucide-react';
import { approvalWorkflowAPI } from '../../services/api';

const DOC_TYPES = [
  { value: 'Indent', label: 'Indent', icon: ClipboardList, color: '#E65100' },
  { value: 'PO', label: 'Purchase Order', icon: ShoppingCart, color: '#1565C0' },
  { value: 'OC', label: 'Order Confirmation', icon: FileText, color: '#7B1FA2' },
];
const ROLES = ['Manager', 'Admin'];
const CONDITION_TYPES = [
  { value: 'amount', label: 'Amount' },
  { value: 'branch', label: 'Branch' },
  { value: 'department', label: 'Department' },
];
const OPERATORS = [
  { value: 'gt', label: '>' }, { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' }, { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
];

const emptyConfig = {
  document_type: 'Indent',
  name: '',
  is_active: true,
  rules: [],
  approval_chain: [{ level: 1, role: 'Manager', label: 'Manager Approval' }],
};

const ApprovalWorkflowsTab = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyConfig });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const data = await approvalWorkflowAPI.getConfigs();
      setConfigs(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Workflow name is required');
    if (form.approval_chain.length === 0) return alert('At least one approval level is required');
    setSaving(true);
    try {
      if (editId) {
        await approvalWorkflowAPI.updateConfig(editId, form);
      } else {
        await approvalWorkflowAPI.createConfig(form);
      }
      setShowForm(false);
      setEditId(null);
      setForm({ ...emptyConfig });
      fetchConfigs();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workflow config?')) return;
    try {
      await approvalWorkflowAPI.deleteConfig(id);
      fetchConfigs();
    } catch (err) { alert('Failed to delete'); }
  };

  const startEdit = (config) => {
    setForm({
      document_type: config.document_type,
      name: config.name,
      is_active: config.is_active,
      rules: config.rules || [],
      approval_chain: config.approval_chain || [],
    });
    setEditId(config.id);
    setShowForm(true);
  };

  const addRule = () => {
    setForm({ ...form, rules: [...form.rules, { condition_type: 'amount', operator: 'gt', value: '' }] });
  };
  const removeRule = (idx) => {
    setForm({ ...form, rules: form.rules.filter((_, i) => i !== idx) });
  };
  const updateRule = (idx, field, value) => {
    const rules = [...form.rules];
    rules[idx] = { ...rules[idx], [field]: value };
    setForm({ ...form, rules });
  };

  const addLevel = () => {
    const nextLevel = form.approval_chain.length + 1;
    setForm({
      ...form,
      approval_chain: [...form.approval_chain, { level: nextLevel, role: 'Admin', label: `Level ${nextLevel} Approval` }],
    });
  };
  const removeLevel = (idx) => {
    const chain = form.approval_chain.filter((_, i) => i !== idx).map((l, i) => ({ ...l, level: i + 1 }));
    setForm({ ...form, approval_chain: chain });
  };
  const updateLevel = (idx, field, value) => {
    const chain = [...form.approval_chain];
    chain[idx] = { ...chain[idx], [field]: value };
    setForm({ ...form, approval_chain: chain });
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Approval Workflows</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Configure rule-based approval chains for Indents, POs, and OCs</p>
        </div>
        <button onClick={() => { setForm({ ...emptyConfig }); setEditId(null); setShowForm(true); }}
          data-testid="add-workflow-btn"
          className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg transition-all" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> New Workflow
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-lg p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-gray-900">{editId ? 'Edit' : 'Create'} Workflow</h4>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1 block">Document Type</label>
              <select value={form.document_type} onChange={e => setForm({ ...form, document_type: e.target.value })}
                data-testid="workflow-doc-type"
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1 block">Workflow Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="workflow-name-input"
                placeholder="e.g., High Value PO Approval" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
          </div>

          {/* Rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Conditions (all must match)</label>
              <button onClick={addRule} className="text-[10px] font-semibold text-blue-600 hover:text-blue-800">+ Add Rule</button>
            </div>
            {form.rules.length === 0 && (
              <p className="text-[11px] text-gray-400 italic py-1">No conditions — this workflow applies to ALL {form.document_type}s</p>
            )}
            {form.rules.map((rule, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1.5">
                <select value={rule.condition_type} onChange={e => updateRule(idx, 'condition_type', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg">
                  {CONDITION_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={rule.operator} onChange={e => updateRule(idx, 'operator', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg w-16">
                  {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input value={rule.value} onChange={e => updateRule(idx, 'value', e.target.value)}
                  placeholder={rule.condition_type === 'amount' ? '50000' : 'Value'}
                  className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                <button onClick={() => removeRule(idx)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3 h-3 text-red-400" /></button>
              </div>
            ))}
          </div>

          {/* Approval Chain */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Approval Chain</label>
              <button onClick={addLevel} className="text-[10px] font-semibold text-blue-600 hover:text-blue-800">+ Add Level</button>
            </div>
            {form.approval_chain.map((level, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1.5 bg-gray-50 p-2 rounded-lg">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: '#24549c' }}>{level.level}</span>
                <select value={level.role} onChange={e => updateLevel(idx, 'role', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input value={level.label} onChange={e => updateLevel(idx, 'label', e.target.value)}
                  placeholder="Level label" className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg" />
                {form.approval_chain.length > 1 && (
                  <button onClick={() => removeLevel(idx)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3 h-3 text-red-400" /></button>
                )}
              </div>
            ))}
          </div>

          {/* Active toggle + Save */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <button onClick={() => setForm({ ...form, is_active: !form.is_active })} className="flex items-center gap-2 text-xs font-medium text-gray-600">
              {form.is_active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
              {form.is_active ? 'Active' : 'Inactive'}
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="save-workflow-btn"
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {editId ? 'Update' : 'Create'} Workflow
            </button>
          </div>
        </div>
      )}

      {/* Config List */}
      {configs.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border border-gray-100">
          <Shield className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-500">No workflows configured</p>
          <p className="text-[11px] text-gray-400 mt-0.5">Create your first approval workflow above</p>
        </div>
      ) : (
        <div className="space-y-2">
          {configs.map(config => {
            const docType = DOC_TYPES.find(t => t.value === config.document_type) || DOC_TYPES[0];
            const Icon = docType.icon;
            const expanded = expandedId === config.id;
            return (
              <div key={config.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`workflow-card-${config.id}`}>
                <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedId(expanded ? null : config.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: docType.color + '14' }}>
                      <Icon className="w-4 h-4" style={{ color: docType.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-bold text-gray-900">{config.name}</p>
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${config.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {config.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500">{docType.label} | {config.approval_chain?.length || 0} level(s) | {config.rules?.length || 0} rule(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); startEdit(config); }} className="p-1.5 rounded-lg hover:bg-blue-50"><Edit2 className="w-3.5 h-3.5 text-blue-500" /></button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(config.id); }} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                    {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>
                {expanded && (
                  <div className="px-4 pb-3 border-t border-gray-50 space-y-2">
                    {config.rules?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Conditions</p>
                        {config.rules.map((r, i) => (
                          <span key={i} className="inline-block mr-1.5 mb-1 px-2 py-1 text-[10px] bg-amber-50 text-amber-800 rounded-md font-medium">
                            {r.condition_type} {OPERATORS.find(o => o.value === r.operator)?.label || r.operator} {r.value}
                          </span>
                        ))}
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Approval Chain</p>
                      <div className="flex items-center gap-1">
                        {config.approval_chain?.map((l, i) => (
                          <React.Fragment key={i}>
                            <span className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-50 text-blue-800 rounded-md font-medium">
                              <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white" style={{ backgroundColor: '#24549c' }}>{l.level}</span>
                              {l.label} ({l.role})
                            </span>
                            {i < config.approval_chain.length - 1 && <span className="text-gray-300 text-xs">→</span>}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalWorkflowsTab;
